import { useEffect, useState } from 'react';
import { BookFormat } from '@masters-club/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  TranslationsForm,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
} from '@/components/translations';
import { FileUploadField, type FileRecord } from './FileUploadField';
import type { BookChapterData, BookChapterTranslationData } from '../types';

const CHAPTER_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Название главы', type: 'input', required: true },
];

interface BookChapterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chapter: BookChapterData | null;
  nextOrder: number;
  format: BookFormat;
  languages: TranslationLanguage[];
  onSave: (chapter: BookChapterData) => void;
}

function chapterToTranslationData(chapter: BookChapterData): TranslationData {
  const data: TranslationData = {};
  for (const t of chapter.translations) {
    data[t.languageCode] = { title: t.title };
  }
  return data;
}

function translationDataToArray(
  data: TranslationData,
  existing: BookChapterTranslationData[],
): BookChapterTranslationData[] {
  const result: BookChapterTranslationData[] = [];
  for (const [code, fields] of Object.entries(data)) {
    const title = fields.title?.trim() ?? '';
    if (!title) { continue; }
    const prev = existing.find((t) => t.languageCode === code);
    result.push({ id: prev?.id, languageCode: code, title });
  }
  return result;
}

function emptyChapter(order: number): BookChapterData {
  return {
    order,
    audioFileId: null,
    audioFileUrl: null,
    audioFileOriginalName: null,
    hlsPlaylistUrl: null,
    durationSec: null,
    pageStart: null,
    pageEnd: null,
    translations: [{ languageCode: 'ru', title: '' }],
  };
}

export function BookChapterDialog({
  open,
  onOpenChange,
  chapter,
  nextOrder,
  format,
  languages,
  onSave,
}: BookChapterDialogProps) {
  const isEditing = chapter !== null;
  const [draft, setDraft] = useState<BookChapterData>(
    () => chapter ?? emptyChapter(nextOrder),
  );
  const [activeLanguage, setActiveLanguage] = useState('ru');
  const [translationData, setTranslationData] = useState<TranslationData>(() =>
    chapter ? chapterToTranslationData(chapter) : { ru: { title: '' } },
  );

  useEffect(() => {
    if (open) {
      const next = chapter ?? emptyChapter(nextOrder);
      setDraft(next);
      setTranslationData(chapterToTranslationData(next));
      setActiveLanguage('ru');
    }
  }, [open, chapter, nextOrder]);

  const isAudio = format === BookFormat.AUDIO;
  const hasTitle = Object.values(translationData).some((t) => t.title?.trim());
  const hasAudio = draft.audioFileId !== null;
  const canSave = hasTitle && (!isAudio || hasAudio);

  const handleAudioChange = (file: FileRecord | null) => {
    setDraft((prev) => ({
      ...prev,
      audioFileId: file?.id ?? null,
      audioFileUrl: file?.url ?? null,
    }));

    // Auto-detect duration via Web Audio API
    if (file?.url) {
      const audio = new Audio(file.url);
      audio.addEventListener('loadedmetadata', () => {
        if (isFinite(audio.duration) && audio.duration > 0) {
          setDraft((prev) => ({ ...prev, durationSec: Math.round(audio.duration) }));
        }
      }, { once: true });
    }
  };

  const handleSave = () => {
    const translations = translationDataToArray(translationData, draft.translations);
    onSave({ ...draft, translations });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Редактировать главу' : 'Новая глава'}</DialogTitle>
          <DialogDescription>
            {isAudio
              ? 'Загрузите аудиофайл и задайте название.'
              : 'Задайте диапазон страниц и название раздела.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Порядок</Label>
            <Input
              type="number"
              min={0}
              value={draft.order}
              onChange={(e) => setDraft((prev) => ({ ...prev, order: parseInt(e.target.value, 10) || 0 }))}
            />
          </div>

          <TranslationsForm
            fields={CHAPTER_FIELDS}
            value={translationData}
            onChange={setTranslationData}
            languages={languages}
            activeLanguage={activeLanguage}
            onLanguageChange={setActiveLanguage}
          />

          {isAudio ? (
            <>
              <div className="space-y-2">
                <Label>Аудиофайл</Label>
                <FileUploadField
                  value={
                    draft.audioFileId && draft.audioFileUrl
                      ? { id: draft.audioFileId, url: draft.audioFileUrl, name: draft.audioFileOriginalName ?? undefined }
                      : null
                  }
                  onChange={handleAudioChange}
                  accept="audio/*"
                  placeholder="Загрузить аудиофайл главы"
                  maxSizeMB={500}
                />
              </div>
              <div className="space-y-2">
                <Label>Длительность (сек)</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.durationSec ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      durationSec: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                  placeholder="Определяется автоматически"
                />
                <p className="text-xs text-muted-foreground">Заполняется автоматически из аудиофайла.</p>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Страница с</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.pageStart ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      pageStart: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Страница по</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.pageEnd ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      pageEnd: e.target.value ? parseInt(e.target.value, 10) : null,
                    }))
                  }
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
